import React, { Fragment, useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/standard-button';
import { MediaClientConfig } from '@atlaskit/media-core';

import { AttachmentEntryItem } from 'src/components/activity/types';
import Loading from 'src/components/loading';
import {
  Attachment,
  MediaAttachmentFilmstrip,
} from 'src/components/media-attachment-filmstrip';
import { useMediaIdContext } from 'src/contexts/media-id';
import { useIntl } from 'src/hooks/intl';

import { messages } from './pull-request-activity-events.i18n';

type Props = {
  items: AttachmentEntryItem[];
};

const AttachmentLink = React.memo(function AttachmentLink({
  name,
  uuid,
}: {
  name: string;
  uuid: string;
}) {
  const { setMediaId } = useMediaIdContext();
  const onSetMediaId = useCallback(() => setMediaId(uuid), [setMediaId, uuid]);

  return (
    <Button appearance="link" onClick={onSetMediaId} spacing="none">
      {name}
    </Button>
  );
});

export const PullRequestAttachmentEntry = ({ items }: Props) => {
  const intl = useIntl();
  const untitledAttachmentName = intl.formatMessage(
    messages.untitledAttachment
  );
  const numberOfAttachments = items.length;
  const MAX_ITEMS_TO_SHOW = 3;
  const hasExtraAttachments = numberOfAttachments > MAX_ITEMS_TO_SHOW;

  return (
    <>
      <FormattedMessage
        {...messages.attachmentAddedMessage}
        values={{ numOfAttachments: numberOfAttachments }}
      />
      {': '}
      {items.slice(0, MAX_ITEMS_TO_SHOW).map((attachment, index) => {
        const count = index + 1;
        const { name, uuid } = attachment;
        const attachmentName = name || untitledAttachmentName;

        return (
          <Fragment key={index}>
            <AttachmentLink name={attachmentName} uuid={uuid} />
            {count !== numberOfAttachments && `,`}
            {/* render ellipsis if total items are more than maximum shown */}
            {count === MAX_ITEMS_TO_SHOW &&
              hasExtraAttachments &&
              ` ${String.fromCharCode(8230)}`}
          </Fragment>
        );
      })}
    </>
  );
};

type PullRequestAttachmentTitleProps = {
  numberOfAttachments: number;
};

export const PullRequestAttachmentTitle = ({
  numberOfAttachments,
}: PullRequestAttachmentTitleProps): JSX.Element => {
  return (
    <FormattedMessage
      {...messages.attachmentAddedMessage}
      values={{ numOfAttachments: numberOfAttachments }}
    />
  );
};

type PullRequestAttachmentBodyProps = {
  isLoading: boolean;
  mediaClientConfig?: MediaClientConfig;
  attachments: AttachmentEntryItem[];
  collectionName: string;
};

export const PullRequestAttachmentBody = (
  props: PullRequestAttachmentBodyProps
): JSX.Element | null => {
  if (props.isLoading) {
    return <Loading size="small" />;
  } else if (props.mediaClientConfig) {
    return (
      <MediaAttachmentFilmstrip
        attachments={props.attachments as Attachment[]}
        collectionName={props.collectionName}
        mediaClientConfig={props.mediaClientConfig}
      />
    );
  }
  return null;
};
